import React, { useSyncExternalStore } from "react";

export const RESTRICTION_DISPLAY_NAMES_PARAM_KEY =
  "_restriction_display_names";

const names = new Map<string, string>();
const listeners = new Set<() => void>();

const emit = (): void => {
  listeners.forEach((listener) => listener());
};

export const subscribeRestrictionDisplayNames = (
  onStoreChange: () => void,
): (() => void) => {
  listeners.add(onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
  };
};

/** Chip titles only — a value equal to the Sportradar ID is not a name. */
export const usableRestrictionDisplayName = (
  id: string | undefined,
  name: string | undefined,
): string | undefined => {
  const trimmedId = id?.trim() ?? "";
  const trimmedName = name?.trim() ?? "";
  if (!trimmedId || !trimmedName || trimmedName === trimmedId) return undefined;
  return trimmedName;
};

export const rememberRestrictionDisplayName = (
  id: string,
  name: string,
): void => {
  const trimmedId = id.trim();
  const trimmedName = usableRestrictionDisplayName(id, name);
  if (!trimmedId || !trimmedName) return;
  if (names.get(trimmedId) === trimmedName) return;
  names.set(trimmedId, trimmedName);
  emit();
};

export const getRestrictionDisplayName = (id: string): string | undefined => {
  const trimmedId = id.trim();
  if (!trimmedId) return undefined;
  return usableRestrictionDisplayName(trimmedId, names.get(trimmedId));
};

export const clearRestrictionDisplayNames = (): void => {
  if (names.size === 0) return;
  names.clear();
  emit();
};

export const useRestrictionDisplayName = (
  id: string | undefined,
): string | undefined => {
  const trimmedId = id?.trim() ?? "";
  return useSyncExternalStore(
    subscribeRestrictionDisplayNames,
    () =>
      trimmedId
        ? usableRestrictionDisplayName(trimmedId, names.get(trimmedId))
        : undefined,
    () => undefined,
  );
};

export const RestrictionDisplayNamesControl: React.FC<{
  value?: Record<string, string>;
  onChange?: (value: Record<string, string>) => void;
}> = () => null;
